/**
 * hooks.ts —— React 与外部 store 的桥接
 *
 * 所有时序逻辑都在 GameController（React 之外），组件只做两件事：
 *   1. 用 useSyncExternalStore 订阅「变了就重渲染」；
 *   2. 把用户意图转成 controller 的方法调用。
 * 这样 StrictMode 的双调用不会重复起定时器，也不需要把可变引擎复制成不可变树。
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Store } from '../state/store';
import { settingsStore, type Settings } from '../state/settings';
import { toastStore } from '../state/toasts';
import { letwayStore, type LetwayState } from '../state/letway';
import type { GameController } from '../state/controller';
import type { EquityPanelState } from '../state/equityService';
import { acquireOverlay } from './overlay';

export function useStoreValue<T>(store: Store<T>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

export function useSettings(): Settings {
  return useStoreValue(settingsStore);
}

export function useToasts() {
  return useStoreValue(toastStore);
}

/** 让位状态（二期 FR-E2/E3）：pwaYield 供 PwaHints 让位、blockAux 供辅助浮层禁触发。 */
export function useLetway(): LetwayState {
  return useStoreValue(letwayStore);
}

/** 订阅 controller 的版本号：任何引擎/编排状态变化都会触发重渲染。 */
export function useGameVersion(c: GameController): number {
  return useStoreValue(c.version);
}

export function useEquityPanel(c: GameController): EquityPanelState {
  return useStoreValue(c.equity.store);
}

/** 定时重渲染（倒计时、结算倒数等纯展示型时间）。ms=0 表示不启用。 */
export function useTicker(ms: number): number {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!ms) return;
    const id = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return 0;
}

/** 当前视口断点（设计方案 5.2 四档）。 */
export type Breakpoint = 'xl' | 'lg' | 'md' | 'sm';

export function useBreakpoint(): Breakpoint {
  const calc = (): Breakpoint => {
    if (typeof window === 'undefined') return 'xl';
    const w = window.innerWidth;
    if (w >= 1280) return 'xl';
    if (w >= 1024) return 'lg';
    if (w >= 768) return 'md';
    return 'sm';
  };
  const [bp, setBp] = useState<Breakpoint>(calc);
  useEffect(() => {
    const onResize = (): void => setBp(calc());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return bp;
}

/**
 * 极矮视口（默认 ≤430px 高）。
 *
 * 为什么要单独一条而不是继续用宽度断点（REG2-NEW-002 根因）：
 *   断点只看宽度，于是 640×360 这种横屏矮视口被判成 `sm` 并沿用竖屏那套「顶栏 + 牌桌 +
 *   页脚三段」预算 —— 实测顶栏 61 + 页脚 213 直接吃掉 274px，牌桌只剩 78px、
 *   对手区实测高度 0（`.felt__seats` clientHeight=0 → 席位算法拿不到盒子、退回 CSS 兜底），
 *   10 个席位全部越出毛毡、可见比 0。高度才是这里的稀缺资源，必须独立判定。
 *
 * 阈值 430 的取法：常见手机横屏短边 360/375/390/414 全部落入，
 * 而所有标准断点（min360 640 高、sm 844、md 1100、lg 800、xl 900）都在阈值之上，
 * 因此这条规则对既有 5 个验收断点零影响。
 */
export function useShortViewport(maxHeight = 430): boolean {
  const calc = (): boolean => (typeof window === 'undefined' ? false : window.innerHeight <= maxHeight);
  const [short, setShort] = useState<boolean>(calc);
  useEffect(() => {
    const onResize = (): void => setShort(calc());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxHeight]);
  return short;
}

/**
 * 竖屏触屏设备（手机 / 平板竖握）判定。
 *
 * 背景（真机布局兼容）：`useBreakpoint` 只看 `window.innerWidth`。部分高分辨率安卓机
 * （如 iQOO 15）在竖屏下上报的 CSS 宽度会落进 `md`（≥768）甚至更高，于是被当成桌面
 * 走了 `oval` 椭圆牌桌 —— 椭圆上沿在窄高比的竖屏里向内收，5 张公共牌带被曲边裁掉
 * （首张公共牌左侧被切、点数看不见）。竖屏触屏本质上就该走 `stack` 三段式布局，公共牌
 * 独占顶行、全宽不裁切，且各人数（2/3/4/5/6）下都稳定。
 *
 * 判据用 `pointer: coarse`（触屏）+ `orientation: portrait`（竖屏）两个媒体特性，
 * 桌面（fine pointer）无论窗口多窄都不受影响，既有 md/lg/xl 椭圆验收零回归；
 * jsdom 默认 matchMedia 返回 false，单测同样不受影响。
 */
export function usePortraitTouch(): boolean {
  const calc = (): boolean => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const portrait = window.matchMedia('(orientation: portrait)').matches;
    return coarse && portrait;
  };
  const [v, setV] = useState<boolean>(calc);
  useEffect(() => {
    const on = (): void => setV(calc());
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('orientationchange', on);
    };
  }, []);
  return v;
}

/**
 * Esc 关闭 + 焦点收拢 + 归还焦点（浮层无障碍最低要求）。
 *
 * @param restoreTo 关闭后要把焦点交还给谁。传入触发浮层的那个按钮最稳，
 *                  因为「打开时的 document.activeElement」在两种常见路径下都不可靠：
 *                  ① 用快捷键打开时它是 body；② 父组件重渲染会让本 effect 重跑，
 *                  再次读取 activeElement 时焦点已经在浮层内部，归还目标被自己污染，
 *                  于是浮层卸载后焦点掉回 body（QA-BUG-020 现场）。
 *
 * 依赖数组刻意只留 `open`：onClose 在调用方通常是行内箭头函数，
 * 若进依赖数组会让「登记浮层 / 抢焦点 / 归还焦点」在每次渲染时反复执行。
 */
export function useDismissable(open: boolean, onClose: () => void, restoreTo?: HTMLElement | null) {
  const ref = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const restoreToRef = useRef<HTMLElement | null>(restoreTo ?? null);
  if (restoreTo !== undefined) restoreToRef.current = restoreTo;

  useEffect(() => {
    if (!open) return;
    const node = ref.current;
    const active = document.activeElement as HTMLElement | null;
    // 只在焦点还在浮层之外时采信 activeElement，避免把浮层自己的按钮记成归还目标
    const outside = active && active !== document.body && (!node || !node.contains(active)) ? active : null;
    restoreRef.current = restoreToRef.current ?? outside;
    const release = acquireOverlay();
    const focusables = (): HTMLElement[] =>
      node
        ? Array.from(
            node.querySelectorAll<HTMLElement>(
              'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => el.offsetParent !== null)
        : [];
    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      release();
      document.removeEventListener('keydown', onKey, true);
      const back = restoreRef.current;
      if (!back || !back.isConnected) return;
      back.focus?.();
      /*
       * 真机补丁（QA-BUG-020）：上面那次同步 focus 在「点击气泡外部」这条路径上会被浏览器吃掉。
       * mousedown 的默认行为是在事件派发**结束之后**重设焦点，而 React 的卸载与本 cleanup
       * 都发生在派发过程中，于是焦点被默认行为改回 body —— e01-confirm 在 Chromium 上实测
       * afterOutside.focus 就是 'body'（jsdom 不模拟这段默认行为，所以单测看不出来）。
       *
       * 因此再补一次下一任务的兜底，且只在「焦点确实无处安放」时才抢回来：
       * 用户若点在另一个可聚焦元素上，那次焦点是用户本意，不能夺走。
       */
      window.setTimeout(() => {
        const now = document.activeElement;
        if ((!now || now === document.body) && back.isConnected) back.focus?.();
      }, 0);
    };
  }, [open]);

  return ref;
}
